(() => {
if (customElements.get('shipping-rates-component')) return;

class ShippingRatesComponent extends HTMLElement {
    constructor() {
        super();
        this.countrySelect = this.querySelector('[data-shipping-country]');
        this.provinceWrapper = this.querySelector('[data-province-wrapper]');
        this.provinceSelect = this.querySelector('[data-shipping-province]');
        this.zipInput = this.querySelector('[data-shipping-zip]');
        this.button = this.querySelector('[data-shipping-rates-button]');
        this.container = this.querySelector('[data-shipping-rates-container]');
        this.list = this.querySelector('[data-shipping-rates-list]');
        this.error = this.querySelector('[data-shipping-rates-error]');
        this.productFormId = this.dataset.productFormId;
        this.onCountryChange = this.handleCountryChange.bind(this);
        this.onButtonClick = this.handleButtonClick.bind(this);
    }

    connectedCallback() {
        if (!this.countrySelect || !this.zipInput || !this.button) return;
        this.selectDefaultCountry();
        this.countrySelect.addEventListener('change', this.onCountryChange);
        this.button.addEventListener('click', this.onButtonClick);
        this.handleCountryChange();
    }

    disconnectedCallback() {
        this.countrySelect?.removeEventListener('change', this.onCountryChange);
        this.button?.removeEventListener('click', this.onButtonClick);
    }

    selectDefaultCountry() {
        const defaultCountry = this.countrySelect?.dataset?.defaultCountry;
        if (!defaultCountry || !this.countrySelect) return;

        const normalizedDefault = defaultCountry.trim().toLowerCase();
        const defaultAliases = [normalizedDefault];

        if (normalizedDefault === 'brasil' || normalizedDefault === 'brazil' || normalizedDefault === 'br') {
            defaultAliases.push('brasil', 'brazil', 'br');
        }

        const option = Array.from(this.countrySelect.options).find((item) => {
            const label = item.textContent?.trim().toLowerCase();
            const value = item.value?.trim().toLowerCase();
            return defaultAliases.includes(label) || defaultAliases.includes(value);
        });

        if (option) this.countrySelect.value = option.value;
    }

    handleCountryChange() {
        if (!this.countrySelect || !this.provinceWrapper || !this.provinceSelect) return;

        const selectedOption = this.countrySelect.options[this.countrySelect.selectedIndex];
        const rawProvinces = selectedOption?.dataset?.provinces || '[]';
        let provinces = [];

        try {
            provinces = JSON.parse(rawProvinces);
        } catch (error) {
            provinces = [];
        }

        this.provinceSelect.innerHTML = '';

        if (!provinces.length) {
            this.provinceWrapper.style.display = 'none';
            return;
        }

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.provinceSelect.dataset.placeholder || '';
        this.provinceSelect.appendChild(placeholder);

        provinces.forEach(([value, label]) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = label;
            this.provinceSelect.appendChild(option);
        });

        this.provinceWrapper.style.display = '';
    }

    async handleButtonClick() {
        const zip = this.zipInput?.value?.trim();

        if (!zip) {
            this.showError(this.dataset.unableToFetchShippingRatesText);
            return;
        }

        if (
            this.provinceWrapper &&
            this.provinceWrapper.style.display !== 'none' &&
            !this.provinceSelect?.value
        ) {
            this.showError(this.provinceSelect?.dataset?.placeholder || this.dataset.unableToFetchShippingRatesText);
            return;
        }

        this.setLoading(true);
        this.hideMessages();

        try {
            const shippingRates = this.productFormId
                ? await this.fetchShippingRatesForProduct()
                : await this.fetchShippingRates();
            this.renderRates(shippingRates);
        } catch (error) {
            this.showError(
                error?.message || this.dataset.unableToFetchShippingRatesText
            );
        } finally {
            this.setLoading(false);
        }
    }

    buildQueryString() {
        const params = new URLSearchParams();
        params.set('shipping_address[country]', this.countrySelect?.value || '');
        params.set('shipping_address[zip]', this.zipInput?.value?.trim() || '');

        if (this.provinceWrapper && this.provinceWrapper.style.display !== 'none') {
            params.set(
                'shipping_address[province]',
                this.provinceSelect?.value || ''
            );
        }

        return params.toString();
    }

    async fetchShippingRates() {
        const root = window.Shopify?.routes?.root || '/';
        const query = this.buildQueryString();
        const shippingRatesUrl = `${root}cart/shipping_rates.json?${query}`;
        const asyncShippingRatesUrl = `${root}cart/async_shipping_rates.json?${query}`;

        const response = await fetch(shippingRatesUrl, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });

        if (response.status === 202) {
            return this.pollAsyncRates(asyncShippingRatesUrl);
        }

        const data = await response.json();
        if (!response.ok) throw new Error(this.extractErrorMessage(data));
        return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
    }

    async fetchShippingRatesForProduct() {
        const form = document.getElementById(this.productFormId);
        const variantId = form?.querySelector('[name="id"]')?.value;
        const quantityInput = form?.querySelector('[name="quantity"]');
        const quantity = Math.max(Number(quantityInput?.value) || 1, 1);

        if (!variantId) {
            throw new Error(this.dataset.unableToFetchShippingRatesText);
        }

        const root = window.Shopify?.routes?.root || '/';
        const cart = await this.fetchCart(root);
        let addedItem;

        try {
            const addResponse = await fetch(`${root}cart/add.js`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    id: Number(variantId),
                    quantity,
                }),
            });

            const addData = await addResponse.json();
            if (!addResponse.ok) throw new Error(this.extractErrorMessage(addData));

            addedItem = addData;
            return await this.fetchShippingRates();
        } finally {
            if (addedItem?.key) {
                const originalLine = cart.items?.find((item) => item.key === addedItem.key);
                await this.restoreCartLine(root, addedItem.key, originalLine?.quantity || 0);
            }
        }
    }

    async fetchCart(root) {
        const response = await fetch(`${root}cart.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });

        if (!response.ok) return { items: [] };
        return response.json();
    }

    async restoreCartLine(root, key, quantity) {
        await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                id: key,
                quantity,
            }),
        });
    }

    async pollAsyncRates(url, attempt = 0) {
        const response = await fetch(url, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });

        if (response.status === 202 && attempt < 6) {
            await new Promise((resolve) => window.setTimeout(resolve, 700));
            return this.pollAsyncRates(url, attempt + 1);
        }

        const data = await response.json();
        if (!response.ok) throw new Error(this.extractErrorMessage(data));
        return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
    }

    extractErrorMessage(data) {
        if (typeof data === 'string') return data;
        if (data?.error) return data.error;
        if (Array.isArray(data?.errors)) return data.errors.join(', ');
        if (typeof data?.errors === 'string') return data.errors;
        return this.dataset.unableToFetchShippingRatesText;
    }

    renderRates(rates) {
        this.container.style.display = '';
        this.error.style.display = 'none';

        if (!rates.length) {
            this.list.innerHTML = `<div class="shipping-rates__error">${this.dataset.noShippingRatesText}</div>`;
            return;
        }

        const headerText =
            rates.length > 1
                ? this.dataset.multipleShippingRatesText
                : this.dataset.oneShippingRateText;

        const items = rates
            .map((rate) => {
                const price = this.formatRatePrice(rate.price);
                const delivery = this.formatDeliveryEstimate(rate);

                return `
                  <li class="shipping-rates__item">
                    <div class="shipping-rates__item-container">
                      <span class="shipping-rates__name">${rate.presentment_name || rate.name}</span>
                      <span class="shipping-rates__price">${price}</span>
                      ${delivery ? `<span class="shipping-rates__delivery-date">${delivery}</span>` : ''}
                    </div>
                  </li>
                `;
            })
            .join('');

        this.list.innerHTML = `
          <div class="shipping-rates__list-container">
            <div class="shipping-rates__list-header">${headerText}</div>
            <ul class="shipping-rates__list">${items}</ul>
          </div>
        `;
    }

    formatRatePrice(price) {
        const numericPrice = Number(price);
        const cents = Number.isNaN(numericPrice)
            ? 0
            : Math.round(numericPrice * 100);

        if (window.Shopify?.formatMoney) {
            return window.Shopify.formatMoney(cents, window.Shopify.money_format);
        }

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(numericPrice);
    }

    formatDeliveryEstimate(rate) {
        const rawValue =
            rate?.delivery_days ??
            rate?.min_delivery_date ??
            rate?.max_delivery_date ??
            '';

        if (!rawValue) return '';

        const value = String(rawValue).trim();
        const matches = value.match(/\d+/g);

        if (!matches || !matches.length) return value;

        const uniqueDays = [...new Set(matches.map((item) => Number(item)).filter((item) => !Number.isNaN(item)))];

        if (!uniqueDays.length) return value;

        if (uniqueDays.length === 1) {
            return this.formatDayLabel(uniqueDays[0]);
        }

        const min = Math.min(...uniqueDays);
        const max = Math.max(...uniqueDays);

        if (min === max) return this.formatDayLabel(min);

        return `${min}-${max} dias`;
    }

    formatDayLabel(days) {
        return `${days} ${days === 1 ? 'dia' : 'dias'}`;
    }

    setLoading(isLoading) {
        this.button.disabled = isLoading;
        this.button.classList.toggle('is-loading', isLoading);
        this.button.classList.toggle('loadingState', isLoading);
    }

    hideMessages() {
        if (this.container) this.container.style.display = 'none';
        if (this.list) this.list.innerHTML = '';
        if (this.error) {
            this.error.style.display = 'none';
            this.error.textContent = '';
        }
    }

    showError(message) {
        this.container.style.display = '';
        this.list.innerHTML = '';
        this.error.textContent = message;
        this.error.style.display = '';
    }
}

customElements.define('shipping-rates-component', ShippingRatesComponent);
})();
